import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CancelIcon from "@mui/icons-material/Cancel";
import dayjs from "dayjs";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Box, Stack } from "@mui/system";
import { useSelector } from "react-redux";
import {
  clearAllTimeUnits,
  clearAllTimeUnitsData,
  clearFilterBizLayerQueryDataMap,
  clearTrendsFilters,
  setActivityNotesDataGlobal,
  setDataSet,
  setFetchBy,
  setSelectedEndDate,
  setSelectedSharedUid,
  setSelectedStartDate,
  setSelectedTimeUnit,
  setShowTrendsTabs,
  setTrendsDashboardExecuteQueriesData,
} from "../../../../features/trendsSlice";
import { useDispatch } from "react-redux";
import { datesDefault } from "./helpers/funcs/datesDefault";
import SimpleBackdrop from "../../../BackDrop/BackDrop";
import {
  getAutocompleteOptions,
  handleAutocompleteChange,
  handleClearAutocomplete,
} from "./helpers/funcs/autocompleteFuncs";
import { useTrendsLogic } from "./helpers/hooks/useGetTrendsAPI";
import TrendsTabs from "./TrendsTabs/TrendsTabs";
import FloatingActionItems from "../../../../pages/FloatingItems/FloatingActionItems";

interface Props {
  oauthTokenHeader: any;
  response: any;
}

const Trends = ({ response }: Props) => {
  const {
    fetchBy,
    dataSet,
    selectedStartDate,
    selectedEndDate,
    organizationDimensions,
    selectedSharedUid,
  } = useSelector((state: any) => state.trends);
  const { userData } = useSelector((state: any) => state.user);
  const dimentionOptions = userData?.data?.dimensions || [];
  const dispatch = useDispatch();

  // items from storage when redirecting from Business leader dashboard

  const [searchParams, setSearchParams] = useSearchParams() as any;
  const dataset = searchParams.get("dataSet");
  const viewTrendsParam = searchParams.get("view-Trends");
  const selectedSiteParam = searchParams.get("selectedSite");
  const dimensionParam = searchParams.get("dimension");
  const [urlParamsLoaded, setUrlParamsLoaded] = useState(false);
  const { isOrgLoading, handleViewTrends, handleOrganizationDimensions } =
    useTrendsLogic();
  const [isLoading, setIsLoading] = useState(false);
  const tabFromQuery = searchParams.get("tab");
  const initialTab = tabFromQuery ? parseInt(tabFromQuery, 10) : 0;
  const [value, setValue] = useState(initialTab);
  const SELECT_ALL_OPTION = { uid: "select_all", siteName: "Select All" };
  const location = useLocation();
  const navigate = useNavigate();

  const handleChange = (_: unknown, newValue: number) => {
    setValue(newValue);
    const newParams = new URLSearchParams(searchParams);
    newParams.set("tab", newValue.toString());
    if (newParams.has("graph")) {
      newParams.delete("graph");
    }
    setSearchParams(newParams);
  };

  const siteOptions =
    response?.website_permissions?.map((siteObj: any) => {
      const uid = Object.keys(siteObj)[0];
      const siteName = siteObj[uid].ui_site_name;
      return { uid, siteName };
    }) || [];

  const timeUnitFromURL = searchParams.get("timeUnit")?.toLowerCase();
  const matchedTimeUnit = datesDefault.find(
    (option) => option.label.toLowerCase() === timeUnitFromURL
  );

  const selectedTime = useMemo(
    () => matchedTimeUnit || datesDefault[0],
    // eslint-disable-next-line
    [matchedTimeUnit, selectedSharedUid, selectedStartDate, selectedEndDate]
  );

  useEffect(() => {
    dispatch(setSelectedTimeUnit(selectedTime));
  }, [dispatch, selectedTime]);

  const selectedSite =
    fetchBy === "site"
      ? siteOptions.filter((site: any) => selectedSharedUid.includes(site.uid))
      : fetchBy === "dimension"
      ? dimentionOptions
          .map((dim: string) => ({ uid: dim, siteName: dim }))
          .filter((site: any) => selectedSharedUid.includes(site.uid))
      : fetchBy === "organization"
      ? organizationDimensions
      : [];

  const onViewTrendsClick = () => {
    if (viewTrendsParam !== "true") {
      dispatch(clearAllTimeUnitsData());
      dispatch(clearAllTimeUnits());
      setIsLoading(true); // Trigger loader only when API will be called

      const newParams = new URLSearchParams(searchParams);
      newParams.set("dataSet", dataSet);
      newParams.set("view-Trends", "true");
      setSearchParams(newParams);
    }
  };
  useEffect(() => {
    const selectedSiteParam = searchParams.get("selectedSite");
    const dimensionParam = searchParams.get("dimension");
    const datasetParam = searchParams.get("dataSet");
    const fetchByParam = searchParams.get("fetchBy");
    const startDateParam = searchParams.get("selectedStartDate");
    const endDateParam = searchParams.get("selectedEndDate");

    if (startDateParam) {
      dispatch(setSelectedStartDate(dayjs(startDateParam, "DD-MM-YYYY")));
    }
    if (endDateParam) {
      dispatch(setSelectedEndDate(dayjs(endDateParam, "DD-MM-YYYY")));
    }
    if (datasetParam) {
      dispatch(setDataSet(datasetParam));
    }
    if (selectedSiteParam) {
      dispatch(setSelectedSharedUid(selectedSiteParam.split(",")));
    } else if (dimensionParam) {
      dispatch(setSelectedSharedUid(dimensionParam.split(",")));
    }

    if (fetchByParam) {
      dispatch(setFetchBy(fetchByParam));
    } else if (dimensionParam) {
      dispatch(setFetchBy("dimension"));
    } else if (selectedSiteParam) {
      dispatch(setFetchBy("site"));
    } else {
      dispatch(setFetchBy("site"));
    }
    setUrlParamsLoaded(true);
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    if (viewTrendsParam === "true") {
      setIsLoading(true); // Immediately show loading UI before fetch starts
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const fetchTrends = async () => {
      if (viewTrendsParam === "true" && urlParamsLoaded) {
        try {
          setIsLoading(true);
          await handleViewTrends(selectedSharedUid); // Assuming it returns a Promise
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchTrends();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewTrendsParam, urlParamsLoaded]);

  useEffect(() => {
    handleOrganizationDimensions();
  }, [handleOrganizationDimensions]);

  const onAutocompleteChange = (newValue: any) => {
    if (newValue.length < (selectedSite?.length || 0)) {
      dispatch(setTrendsDashboardExecuteQueriesData([]));
      dispatch(setActivityNotesDataGlobal([]));
      dispatch(clearTrendsFilters());
      dispatch(clearFilterBizLayerQueryDataMap());
      dispatch(clearAllTimeUnitsData());
    }

    handleAutocompleteChange(
      newValue,
      fetchBy,
      searchParams,
      dispatch,
      setValue,
      setSearchParams,
      datesDefault,
      siteOptions,
      dimentionOptions,
      organizationDimensions
    );
  };
  const onInputChange = (_: any, __: any, reason: string) => {
    if (reason === "clear") {
      handleClearAutocomplete(dispatch);
      dispatch(clearFilterBizLayerQueryDataMap());
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let shouldReplace = false;

    [
      "point_date",
      "values_of_point_date",
      "widget_name",
      "full_widget_name",
      "value_suffix",
      "groupBy",
      "trafficSourceBD",
      "keywordSearch",
      "filters",
      "brandSearch",
      "metric_name",
      "fetch_by",
      "default_website_uid",
      "default_dimension",
      "page",
      //Add as per requirement
    ].forEach((key) => {
      if (params.has(key)) {
        params.delete(key);
        shouldReplace = true;
      }
    });

    if (shouldReplace) {
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  return (
    <>
      <Paper className="monitoringFiltersWrap" sx={{ padding: "8px 16px" }}>
        <Stack direction="row" spacing={2} justifyContent={"space-between"}>
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontSize: "24px",
                paddingBottom: "4px",
                paddingTop: "12px",
              }}
            >
              Trends
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "14px" }}>
              Follow and examine different metrics over time to make educated
              and thougtout.
            </Typography>
          </Box>
          <Stack mt={2}>
            <Box
              gap={2}
              sx={{ display: "flex", alignItems: "center", flexWrap: "nowrap" }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#000", minWidth: "70px" }}
              >
                Fetch by:
              </Typography>
              <RadioGroup
                row
                value={fetchBy}
                onChange={(e) => {
                  const value = e.target.value;
                  dispatch(setFetchBy(value));
                  dispatch(setSelectedSharedUid([]));
                  dispatch(setShowTrendsTabs(false));
                  dispatch(setSelectedTimeUnit(datesDefault[0])); // reset time unit
                  dispatch(setTrendsDashboardExecuteQueriesData([]));
                  dispatch(setActivityNotesDataGlobal([]));
                  dispatch(clearTrendsFilters());
                  dispatch(clearAllTimeUnitsData());
                  const newParams = new URLSearchParams(searchParams);
                  if (value === "organization") {
                    newParams.set("fetchBy", value);
                  } else {
                    newParams.delete("fetchBy");
                  }
                  // Remove conflicting values
                  newParams.delete("selectedSite");
                  newParams.delete("dimension");
                  newParams.delete("tab");
                  newParams.delete("timeUnit");
                  newParams.delete("dataSet");
                  newParams.delete("view-Trends");
                  newParams.delete("graph");
                  const filterParamKeys = [
                    "trafficSource",
                    "campaignId",
                    "adgroupId",
                    "kwId",
                    "country",
                  ];

                  filterParamKeys.forEach((key) => newParams.delete(key));
                  [...newParams.keys()].forEach((key) => {
                    if (key.includes("timeUnit")) {
                      newParams.delete(key);
                    }
                  });
                  setSearchParams(newParams);
                }}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "nowrap",
                }}
              >
                <FormControlLabel
                  value="site"
                  control={
                    <Radio
                      sx={{
                        color: "#9C27B0",
                        "&.Mui-checked": { color: "#9C27B0" },
                        padding: "8px",
                      }}
                    />
                  }
                  label="Website"
                  data-tracker="475"
                />
                <FormControlLabel
                  value="dimension"
                  control={
                    <Radio
                      sx={{
                        color: "#9C27B0",
                        "&.Mui-checked": { color: "#9C27B0" },
                      }}
                    />
                  }
                  label="Dimension"
                  data-tracker="476"
                />
                <FormControlLabel
                  value="organization"
                  control={
                    <Radio
                      sx={{
                        color: "#9C27B0",
                        "&.Mui-checked": { color: "#9C27B0" },
                      }}
                    />
                  }
                  label="Organization"
                  data-tracker="477"
                  disabled //When it needs to enable we have to keep in mind that TrendsBD page we have implement in title also, be careful to keep it sync
                />
              </RadioGroup>
            </Box>
            <Box
              gap={2}
              sx={{ display: "flex", alignItems: "center", flexWrap: "nowrap" }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#000", minWidth: "70px" }}
              >
                Data set:
              </Typography>
              <RadioGroup
                row
                value={dataSet}
                onChange={(e) => {
                  const value = e.target.value;
                  dispatch(setDataSet(value));
                  dispatch(setTrendsDashboardExecuteQueriesData([]));
                  dispatch(setActivityNotesDataGlobal([]));
                  dispatch(clearTrendsFilters());
                  dispatch(clearAllTimeUnitsData());
                  const newParams = new URLSearchParams(searchParams);
                  newParams.delete("dataSet");
                  newParams.delete("view-Trends");
                  newParams.delete("graph");
                  const filterParamKeys = [
                    "trafficSource",
                    "campaignId",
                    "adgroupId",
                    "kwId",
                    "country",
                  ];

                  filterParamKeys.forEach((key) => newParams.delete(key));
                  [...newParams.keys()].forEach((key) => {
                    if (key.includes("timeUnit")) {
                      newParams.delete(key);
                    }
                  });
                  setSearchParams(newParams);
                }}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "nowrap",
                }}
              >
                <FormControlLabel
                  value="action"
                  control={
                    <Radio
                      sx={{
                        color: "#9C27B0",
                        "&.Mui-checked": { color: "#9C27B0" },
                      }}
                    />
                  }
                  label="Action date"
                  data-tracker="478"
                />
                <FormControlLabel
                  value="click"
                  control={
                    <Radio
                      sx={{
                        color: "#9C27B0",
                        "&.Mui-checked": { color: "#9C27B0" },
                      }}
                    />
                  }
                  label="Click date"
                  data-tracker="479"
                />
              </RadioGroup>
            </Box>
          </Stack>
        </Stack>
        <Grid container spacing={2} pt={1}>
          <Grid item xs={12} md={6} sm={6}>
            <Autocomplete
              id="assetOrSiteName"
              multiple
              disablePortal
              disabled={isOrgLoading || fetchBy === "organization"}
              options={[
                ...(getAutocompleteOptions(
                  fetchBy,
                  siteOptions,
                  dimentionOptions,
                  organizationDimensions,
                  selectedSite || []
                ) || SELECT_ALL_OPTION),
              ]}
              getOptionLabel={(option: any) =>
                option.siteName || option.uid || ""
              }
              isOptionEqualToValue={(option, value) =>
                option?.uid === value?.uid
              }
              value={selectedSite || []}
              fullWidth
              onChange={(_, newValue) => onAutocompleteChange(newValue)}
              onInputChange={onInputChange}
              renderTags={(value, getTagProps) =>
                value.map((option: any, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option?.uid}
                    label={option?.siteName}
                    sx={{
                      backgroundColor: "#7B1FA2",
                      color: "#ffffff",
                      height: "24px",
                    }}
                    deleteIcon={
                      <CancelIcon
                        sx={{
                          color: "rgba(255, 255, 255, 0.8) !important",
                          fontSize: "16px !important",
                        }}
                      />
                    }
                  />
                ))
              }
              size="small"
              sx={{ pt: "8px" }}
              renderInput={(params) => (
                <TextField
                  color="secondary"
                  {...params}
                  required
                  label={
                    fetchBy === "site"
                      ? "Select Website"
                      : fetchBy === "dimension"
                      ? "Select Dimension"
                      : "Select Organization Dimension"
                  }
                  placeholder={
                    fetchBy === "site"
                      ? "Search site name or choose from the list"
                      : fetchBy === "dimension"
                      ? "Search dimension or choose from the list"
                      : "Search org dimension or choose from the list"
                  }
                />
              )}
              renderOption={(props, option) => (
                <li
                  {...props}
                  style={{
                    backgroundColor:
                      option.siteName === SELECT_ALL_OPTION.siteName
                        ? "#9C27B00A"
                        : "inherit",
                    color:
                      option.siteName === SELECT_ALL_OPTION.siteName
                        ? "#9C27B0"
                        : "inherit",
                    fontWeight:
                      option.siteName === SELECT_ALL_OPTION.siteName
                        ? "500"
                        : "normal",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#9C27B00A";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      option.siteName === SELECT_ALL_OPTION.siteName
                        ? "#9C27B00A"
                        : "inherit";
                  }}
                >
                  {option.siteName}
                </li>
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={["DatePicker"]}>
                <DatePicker
                  label="Start date"
                  value={selectedStartDate}
                  onChange={(newValue: any) => {
                    dispatch(setTrendsDashboardExecuteQueriesData([]));
                    dispatch(setActivityNotesDataGlobal([]));
                    dispatch(clearTrendsFilters());
                    dispatch(clearFilterBizLayerQueryDataMap());
                    dispatch(clearAllTimeUnitsData());
                    const newParams = new URLSearchParams(searchParams);
                    if (newValue && newValue.isValid()) {
                      dispatch(setSelectedStartDate(newValue)); // Keep it as a dayjs object
                      newParams.set(
                        "selectedStartDate",
                        newValue.format("DD-MM-YYYY")
                      );
                      newParams.delete("tab");
                      newParams.delete("timeUnit");
                      newParams.delete("dataSet");
                      newParams.delete("view-Trends");
                      newParams.delete("graph");
                      const filterParamKeys = [
                        "trafficSource",
                        "campaignId",
                        "adgroupId",
                        "kwId",
                        "country",
                      ];

                      filterParamKeys.forEach((key) => newParams.delete(key));
                      [...newParams.keys()].forEach((key) => {
                        if (key.includes("timeUnit")) {
                          newParams.delete(key);
                        }
                      });
                      setSearchParams(newParams);
                    }
                  }}
                  format="DD-MM-YYYY"
                  slotProps={{
                    textField: {
                      size: "small",
                      color: "secondary",
                      required: true,
                      fullWidth: true,
                      sx: { minWidth: "100% !important" },
                    },
                  }}
                />
              </DemoContainer>
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={["DatePicker"]}>
                <DatePicker
                  label="End Date"
                  value={selectedEndDate}
                  onChange={(newValue: any) => {
                    dispatch(setTrendsDashboardExecuteQueriesData([]));
                    dispatch(setActivityNotesDataGlobal([]));
                    dispatch(clearTrendsFilters());
                    dispatch(clearFilterBizLayerQueryDataMap());
                    dispatch(clearAllTimeUnitsData());
                    const newParams = new URLSearchParams(searchParams);
                    if (newValue && newValue.isValid()) {
                      dispatch(setSelectedEndDate(newValue)); // Keep it as a dayjs object
                      newParams.set(
                        "selectedEndDate",
                        newValue.format("DD-MM-YYYY")
                      );
                      newParams.delete("tab");
                      newParams.delete("timeUnit");
                      newParams.delete("dataSet");
                      newParams.delete("view-Trends");
                      newParams.delete("graph");
                      const filterParamKeys = [
                        "trafficSource",
                        "campaignId",
                        "adgroupId",
                        "kwId",
                        "country",
                      ];

                      filterParamKeys.forEach((key) => newParams.delete(key));
                      [...newParams.keys()].forEach((key) => {
                        if (key.includes("timeUnit")) {
                          newParams.delete(key);
                        }
                      });
                      setSearchParams(newParams);
                    }
                  }}
                  format="DD-MM-YYYY" // Set the display format
                  slotProps={{
                    textField: {
                      size: "small",
                      color: "secondary",
                      required: true,
                      fullWidth: true,
                      sx: { minWidth: "100% !important" },
                    },
                  }}
                />
              </DemoContainer>
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              sx={{ marginTop: "5px" }}
              size="large"
              fullWidth
              onClick={onViewTrendsClick}
              disabled={
                !selectedSite || selectedSite.length === 0 || isOrgLoading
              }
              variant="contained"
              color="secondary"
              data-tracker="97"
            >
              View Trends
            </Button>
          </Grid>
        </Grid>
      </Paper>
      {isLoading ? (
        <SimpleBackdrop />
      ) : dataset !== null ? (
        <>
          <TrendsTabs value={value} onChange={handleChange} />
          <div data-tracker="397">
            {fetchBy === "site" && selectedSiteParam?.length > 0 && (
              <FloatingActionItems
                selectedSiteFloatingAction={selectedSiteParam.split(",")[0]}
                selectedDimensionsFloatingAction=""
                trackIdFloatingAction="427"
              />
            )}

            {fetchBy === "dimension" && dimensionParam?.length > 0 && (
              <FloatingActionItems
                selectedSiteFloatingAction=""
                selectedDimensionsFloatingAction={dimensionParam.split(",")[0]}
                trackIdFloatingAction="427"
              />
            )}
          </div>
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Typography
            variant="body2"
            sx={{ mt: 3, textAlign: "center", color: "#888" }}
          >
            No data available. Please select an asset, dimension, or
            organization and click "View Trends".
          </Typography>
        </Box>
      )}
    </>
  );
};

export default Trends;
